import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Heart, ExternalLink, Users, Globe, ArrowRight, Share2, Award, Droplets, GraduationCap, ShieldCheck } from 'lucide-react';
import { PageTransition } from '../components/PageTransition';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
export function MillionMovementPage() {
  return <PageTransition data-id="element-531" data-mp="32ncfelh66r4m">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden min-h-[70vh] flex items-center" data-id="element-532" data-mp="32q8eid4r7lun">
        <div className="absolute inset-0" data-id="element-533" data-mp="2xrdux8yypt9h">
          <img src="https://cdn.magicpatterns.com/uploads/qHJ8caWqcsCxLWFqzpKR82/jwxld-12-scaled.webp" alt="Jeremy Willet speaking on stage" className="w-full h-full object-cover" data-id="element-534" data-mp="2sxsula9wcn74" />
          <div className="absolute inset-0 bg-dark-900/70" data-id="element-535" data-mp="3lggcyhxido4i"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-536" data-mp="3gn40t7a0n9sn">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-537" data-mp="2b7a106qzxkt2">
            <span className="text-gold-500 font-medium tracking-wider uppercase text-sm mb-4 block" data-id="element-538" data-mp="26gtnkmn6dz6p">
              Child Sponsorship
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 leading-tight" data-id="element-539" data-mp="1sg919gi1xl8g">
              The Million <span className="text-gold-500" data-id="element-540" data-mp="1xe2a6haqotcf">Movement</span>
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-541" data-mp="1ikfktm17p7le">
              A team of storytellers driving an innovative approach to child
              sponsorship to see one million children sponsored by 2050.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-542" data-mp="3hwichjjzp8a5">
              <a href="https://themillionmovement.com/" target="_blank" rel="noopener noreferrer" data-id="element-543" data-mp="3gejp4z5moe78">
                <Button size="lg" rightIcon={<ExternalLink className="w-5 h-5" data-id="element-545" data-mp="36hoqmg9cudsy" />} data-id="element-544" data-mp="3bef8980t5hgh">
                  Visit Official Website
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-dark-700" data-id="element-546" data-mp="31rcxxbzinpv">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-547" data-mp="3u799z09f7d2r">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-548" data-mp="3peznxvbfex5r">
            <Globe className="w-12 h-12 text-gold-500 mx-auto mb-6" data-id="element-549" data-mp="ls96g44n5nca" />
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6" data-id="element-550" data-mp="gy4ypinjxba5">
              One Million Children by 2050
            </h2>
            <p className="text-lg text-white/80 leading-relaxed mb-6" data-id="element-551" data-mp="24l97efgemrmh">
              Founded by Child Advocate Jeremy Willet, The Million Movement
              produces events and award-winning campaigns that travel on
              nationwide concert tours to find sponsors for children living in
              poverty. We believe in the power of art, child sponsorship, and
              dignity for every child.
            </p>
            <p className="text-lg text-white/80 leading-relaxed mb-6" data-id="element-552" data-mp="2njd8cmm51kd7">
              To align with the Sustainable Development Goals to eradicate all
              forms of poverty, we partner with the largest Non-Governmental
              Organizations (NGOs) to create custom, first-hand experience
              visual campaigns.
            </p>
            <div className="w-24 h-1 bg-gold-500 mx-auto rounded-full mb-10" data-id="element-553" data-mp="319yt6gv5k4dq"></div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-554" data-mp="368s72aecpxef">
              <div className="flex items-center gap-3 px-5 py-3 bg-dark-800 border border-gold-500/30 rounded-lg" data-id="element-555" data-mp="2smcpci3zc8wq">
                <Award className="w-5 h-5 text-gold-500" data-id="element-556" data-mp="2nn8dtktl1z38" />
                <span className="text-white/90 text-sm font-medium" data-id="element-557" data-mp="32cns41bxgc21">
                  Bronze Anthem Award — Humanitarian Action & Services
                  Fundraising (2024)
                </span>
              </div>
              <div className="flex items-center gap-3 px-5 py-3 bg-dark-800 border border-gold-500/30 rounded-lg" data-id="element-558" data-mp="29pjuwij2t20t">
                <Award className="w-5 h-5 text-gold-500" data-id="element-559" data-mp="24sl1613bqx35" />
                <span className="text-white/90 text-sm font-medium" data-id="element-560" data-mp="2jo913e1m0y18">
                  Community Voice Anthem Award (2024)
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Sponsorship Supports */}
      <section className="py-24 bg-dark-800 border-t border-dark-600" data-id="element-561" data-mp="3q42z47enfhjm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-562" data-mp="3q5w3fg1shpog">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-3xl mx-auto mb-16" data-id="element-563" data-mp="1g3ldgu0l1ak">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-dark-500 shadow-2xl" data-id="element-564" data-mp="3sq2bnzxfs2at">
              <img src="https://cdn.magicpatterns.com/uploads/5onSWpK5fB6pgy4PvwwtQ5/IMG_0776.jpg" alt="Child sponsorship through The Million Movement" className="w-full h-full object-cover" data-id="element-565" data-mp="kaikd6gbstye" />
            </div>
          </motion.div>

          <div className="text-center mb-16" data-id="element-566" data-mp="ffd2qjtyi1g4">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-567" data-mp="136s3w99zycps">
              What Child Sponsorship Supports
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto" data-id="element-568" data-mp="17zuzso28xy95">
              Child sponsorship helps support essential needs for every child
              and family.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-569" data-mp="1dq18bj7tuxll">
            {[{
            title: 'Clean Water',
            description: 'Access to safe, clean drinking water — the foundation of health and well-being for every child.',
            icon: <Droplets className="w-6 h-6 text-gold-500" data-id="element-570" data-mp="ywppyibe1501" />
          }, {
            title: 'Nutrition',
            description: 'Proper meals and nutritional support to fuel growing bodies and minds.',
            icon: <Heart className="w-6 h-6 text-gold-500" data-id="element-571" data-mp="l4rl4joplv9o" />
          }, {
            title: 'Education',
            description: 'Quality schooling and learning resources that open doors to a brighter future.',
            icon: <GraduationCap className="w-6 h-6 text-gold-500" data-id="element-572" data-mp="11lx4p94y82t" />
          }, {
            title: 'Healthcare & Protection',
            description: 'Medical care and child protection services to keep every child safe and healthy.',
            icon: <ShieldCheck className="w-6 h-6 text-gold-500" data-id="element-573" data-mp="fymepzxm4mnk" />
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-574" data-mp="3egs0houc8qmg" data-mpi={index}>
                <Card className="p-6 h-full bg-dark-700 border-dark-600 hover:border-gold-500/50 transition-colors" data-id="element-575" data-mp="2vp8z1mfypkmj">
                  <div className="w-12 h-12 rounded-full bg-dark-800 border border-dark-500 flex items-center justify-center mb-4" data-id="element-576" data-mp="30mqzeofwyrix">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-serif font-bold text-white mb-3" data-id="element-577" data-mp="eq5dusxo5hxc">
                    {item.title}
                  </h3>
                  <p className="text-white/80 text-sm leading-relaxed" data-id="element-578" data-mp="npaec4ssjlc5">
                    {item.description}
                  </p>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden bg-dark-700" data-id="element-579" data-mp="xmyc73lp18yj">
        <div className="absolute inset-0 bg-gold-600/5" data-id="element-580" data-mp="1bda34z6jl1o3"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-581" data-mp="16fs543u2c6uv">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-10" data-id="element-582" data-mp="16huqubo6ndts">
            <div className="max-w-sm mx-auto aspect-square rounded-2xl overflow-hidden border border-dark-500 shadow-2xl" data-id="element-583" data-mp="1zxn9gpbocct0">
              <img src="https://cdn.magicpatterns.com/uploads/nRfzPtWB3GBG8FpKwbncaP/IMG_0553.jpg" alt="Child sponsorship — Sponsor a child today" className="w-full h-full object-cover" data-id="element-584" data-mp="1q1tmsd48r2xi" />
            </div>
          </motion.div>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6" data-id="element-585" data-mp="1l1yvw9kgudcg">
            Sponsor a Child Today
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-586" data-mp="17bvepx0g19tv">
            Join The Million Movement and help change a child's life through
            sponsorship. Together, we can reach one million children by 2050.
          </p>
          <a href="https://themillionmovement.com/" target="_blank" rel="noopener noreferrer" className="inline-block" data-id="element-587" data-mp="xepnmortxitn">
            <Button size="lg" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-589" data-mp="3jhdvnpdspppa" />} data-id="element-588" data-mp="3jdgdb5z1l26g">
              Explore The Million Movement
            </Button>
          </a>
        </div>
      </section>
    </PageTransition>;
}